import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import { Typography, Button } from '@mui/material';
import EcosystemIntegratorsColumn from './ecosystemPartners.column';
import { useNavigate } from 'react-router-dom';
import Loader from '../../components/loader';
import GenericDataGrid from '../../components/genericTable';
import { Icon } from "@iconify/react";
import { getAllEcosytemIntegator } from './ecosystemPartners.service'
import { EcosytemIntegratorConstants } from '../../constant/ecosystem/ecosystemConst'
import { createManagementTableSx } from '../../styles/tableStyles';
import { managementCreateButtonSx, managementHeaderActionSx, managementHeaderContentSx, managementHeaderSx, managementPageSubtitleSx, managementPageTitleSx } from '../../styles/managementStyles';
import AlertToast from '../../components/alertToast';
import { buttonContentSx } from '../../styles/dialogStyles';
import InlineAlert from '../../components/inlineAlert';
import { FilterChips, FilterSearchField, FilterSelect } from '../../components/filterControls';
import useUrlFilters from '../../components/useUrlFilters';
import { clearFiltersButtonSx, filterBarSx } from '../../styles/filterStyles';
import { integratorTypeCode, integratorTypeLabel } from './integratorType';

// The list endpoint caps a page at 100. Integrators number in the tens, so the screen loads them
// all and filters in the browser: every filter then shows exact counts, and search needs no server
// round trip.
const PAGE_SIZE = 100;

const TYPE_ORDER = ['AREA_BASED', 'INITIATIVE_BASED', 'CROP_BASED', 'FINANCE_BASED'];

const FILTER_KEYS = ['q', 'type', 'country', 'ecosystem'];

const ECOSYSTEM_LABEL = { OPEN: 'Open ecosystem', CLOSED: 'Closed ecosystem' };
const ecosystemOf = (integrator) => (integrator.isOpen ? 'OPEN' : 'CLOSED');

const countBy = (rows, keyOf) => {
    const counts = new Map();
    rows.forEach((row) => {
        const key = keyOf(row);
        counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
};

export default function EcosystemPartners() {

    const navigate = useNavigate();
    const { ECOSYSTEMLIST } = EcosytemIntegratorConstants()
    const [allIntegrators, setAllIntegrators] = useState([]);
    const [errors, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [currenntPagemain, setCurrentPagemain] = useState(0);
    const [rowsperpagemain, setrowsperpagemain] = useState(10);
    const [alert, setAlert] = useState('');
    const [openAlert, setOpenAlert] = useState(false);
    const [responseMsg, setResponseMsg] = useState('');

    // Filters live in the URL, so opening an integrator and coming Back returns to the same list.
    const [filters, setFilters, resetFilters] = useUrlFilters(FILTER_KEYS);
    const { type: typeFilter, country: countryFilter, ecosystem: ecosystemFilter } = filters;
    const search = filters.q.trim().toLowerCase();
    const [searchInput, setSearchInput] = useState(filters.q);

    const alertClose = () => {
        setAlert('');
        setOpenAlert(false);
    };

    // Also the refresh after a delete (the delete modal calls it with no arguments).
    const getEcosytem = useCallback(async () => {
        setLoading(true);
        const first = await getAllEcosytemIntegator(0, PAGE_SIZE);
        if (!first?.data?.content) {
            setLoading(false);
            setAllIntegrators([]);
            setError(first?.message || 'Could not load the ecosystem integrators');
            return;
        }
        let rows = [...first.data.content];
        const pageCount = Math.ceil((first.data.totalElements || 0) / PAGE_SIZE);
        if (pageCount > 1) {
            const rest = await Promise.all(
                Array.from({ length: pageCount - 1 }, (_, i) => getAllEcosytemIntegator(i + 1, PAGE_SIZE))
            );
            rest.forEach((res) => { if (res?.data?.content) rows = rows.concat(res.data.content); });
        }
        setLoading(false);
        setError('');
        setAllIntegrators(rows.sort((a, b) => (a.integratorName || '').localeCompare(b.integratorName || '')));
    }, []);

    useEffect(() => {
        getEcosytem();
    }, [getEcosytem]);

    useEffect(() => {
        if (searchInput === filters.q) return undefined;
        const timer = setTimeout(() => setFilters({ q: searchInput }), 300);
        return () => clearTimeout(timer);
    }, [searchInput, filters.q, setFilters]);

    useEffect(() => {
        setSearchInput((current) => (current.trim() === filters.q.trim() ? current : filters.q));
    }, [filters.q]);

    // One predicate per filter; each option's count is the rows it would leave with the OTHER
    // filters as they are, so the numbers always add up to what the table shows.
    const predicates = useMemo(() => ({
        search: (row) => {
            if (!search) return true;
            const phone = String(row.phoneNumber || '').trim();
            const dialCode = String(row.countryCode || '').trim();
            return [
                row.integratorName,
                row.email,
                row.countryName,
                row.licenseName,
                phone,
                dialCode && phone ? `${dialCode} ${phone}` : '',
                dialCode && phone ? `${dialCode}${phone}` : '',
            ].filter(Boolean).join(' ').toLowerCase().includes(search);
        },
        type: (row) => !typeFilter || integratorTypeCode(row.integratorType) === typeFilter,
        country: (row) => !countryFilter || (row.countryName || '') === countryFilter,
        ecosystem: (row) => !ecosystemFilter || ecosystemOf(row) === ecosystemFilter,
    }), [search, typeFilter, countryFilter, ecosystemFilter]);

    const rowsExcept = useCallback(
        (skip) => allIntegrators.filter((row) =>
            Object.entries(predicates).every(([name, matches]) => name === skip || matches(row))),
        [allIntegrators, predicates]
    );

    const typeOptions = useMemo(() => {
        const scope = rowsExcept('type');
        const counts = countBy(scope, (row) => integratorTypeCode(row.integratorType));
        const codes = [
            ...TYPE_ORDER,
            ...Array.from(counts.keys()).filter((code) => code && !TYPE_ORDER.includes(code)),
        ];
        return [
            { value: '', label: 'All', count: scope.length },
            ...codes
                .filter((code) => counts.get(code) || code === typeFilter)
                .map((code) => ({ value: code, label: integratorTypeLabel(code), count: counts.get(code) || 0 })),
        ];
    }, [rowsExcept, typeFilter]);

    const countryOptions = useMemo(() => {
        const counts = countBy(rowsExcept('country').filter((row) => row.countryName), (row) => row.countryName);
        if (countryFilter && !counts.has(countryFilter)) counts.set(countryFilter, 0);
        return Array.from(counts.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([value, count]) => ({ value, label: value, count }));
    }, [rowsExcept, countryFilter]);

    const ecosystemOptions = useMemo(() => {
        const counts = countBy(rowsExcept('ecosystem'), ecosystemOf);
        return ['OPEN', 'CLOSED'].map((value) => ({ value, label: ECOSYSTEM_LABEL[value], count: counts.get(value) || 0 }));
    }, [rowsExcept]);

    const visibleIntegrators = useMemo(() => rowsExcept(null), [rowsExcept]);

    const hasActiveFilters = Boolean(search || typeFilter || countryFilter || ecosystemFilter);

    const clearFilters = () => {
        setSearchInput('');
        resetFilters();
    };

    useEffect(() => {
        setCurrentPagemain(0);
    }, [search, typeFilter, countryFilter, ecosystemFilter]);

    // A delete can shrink the list past the current page; step back to the last page with rows.
    useEffect(() => {
        const lastPage = Math.max(0, Math.ceil(visibleIntegrators.length / rowsperpagemain) - 1);
        if (currenntPagemain > lastPage) setCurrentPagemain(lastPage);
    }, [visibleIntegrators.length, rowsperpagemain, currenntPagemain]);

    const pageRows = useMemo(() => {
        const start = currenntPagemain * rowsperpagemain;
        return visibleIntegrators.slice(start, start + rowsperpagemain);
    }, [visibleIntegrators, currenntPagemain, rowsperpagemain]);

    let ecosystemData = {
        'limit': '',
        'page': '',
        'navigate': navigate,
        'getEcosytem': getEcosytem,
        'setAlert': setAlert,
        'setOpenAlert': setOpenAlert,
        'setResponseMsg': setResponseMsg,
        'setLoading': setLoading,
    }
    const countryManagementColumn = EcosystemIntegratorsColumn(ecosystemData);

    const ecosystemGridSx = createManagementTableSx();

    return (
        <>
            <Box sx={managementHeaderSx}>
                <Box sx={managementHeaderContentSx}>
                    <Typography className="app-page-title" sx={managementPageTitleSx}>
                        {ECOSYSTEMLIST.HEADER}
                    </Typography>
                    <Typography sx={managementPageSubtitleSx}>
                        Create and manage your ecosystem integrators.
                    </Typography>
                </Box>
                <Box sx={managementHeaderActionSx}>
                    <Button
                        variant="contained"
                        type="submit"
                        onClick={() => { setLoading(true); navigate('/ecosystemPartnersCreation') }}
                        sx={{ ...managementCreateButtonSx, width: { xs: '100%', md: 'auto' }, minWidth: { xs: '100%', md: 240 } }}
                    >
                        <Box component="span" sx={buttonContentSx}>
                            <Icon icon="tabler:plus" width="18" height="18" />
                            <span>CREATE INTEGRATOR</span>
                        </Box>
                    </Button>
                </Box>
            </Box>

      <InlineAlert
        message={openAlert && alert === 'false' ? responseMsg : ''}
        onClose={alertClose}
        variant="error"
      />

            <FilterChips
                options={typeOptions}
                value={typeFilter}
                onChange={(value) => setFilters({ type: value })}
                ariaLabel="Filter by integrator type"
                testId="integrator-type-filter"
            />

            <Box sx={filterBarSx}>
                <FilterSearchField
                    value={searchInput}
                    onChange={setSearchInput}
                    placeholder="Search by name, email, phone or licence"
                    testId="integrator-search"
                />

                <FilterSelect
                    value={countryFilter}
                    onChange={(value) => setFilters({ country: value })}
                    placeholder="All countries"
                    options={countryOptions}
                    testId="integrator-country-filter"
                />

                <FilterSelect
                    value={ecosystemFilter}
                    onChange={(value) => setFilters({ ecosystem: value })}
                    placeholder="Open and closed"
                    options={ecosystemOptions}
                    minWidth={200}
                    testId="integrator-ecosystem-filter"
                />

                {hasActiveFilters && (
                    <Button
                        onClick={clearFilters}
                        startIcon={<Icon icon="material-symbols:filter-alt-off-outline-rounded" width="18" height="18" />}
                        sx={clearFiltersButtonSx}
                    >
                        Clear filters
                    </Button>
                )}
            </Box>

            <Typography sx={{ mb: 1.5, fontSize: 14, lineHeight: '20px', letterSpacing: '0.1px', color: '#6C6A86' }}>
                {hasActiveFilters
                    ? `Showing ${visibleIntegrators.length} of ${allIntegrators.length} integrator${allIntegrators.length === 1 ? '' : 's'}`
                    : `${allIntegrators.length} integrator${allIntegrators.length === 1 ? '' : 's'}`}
            </Typography>

            <Box sx={{ width: '100%', padding: '0 0px' }}>
                <GenericDataGrid rows={pageRows} columns={countryManagementColumn} rowCount={visibleIntegrators.length} page={currenntPagemain} pageSize={rowsperpagemain} loading={loading}
                    errors={errors || (hasActiveFilters && visibleIntegrators.length === 0 ? 'No integrators match your search or filters' : '')}
                    checkbox='false'
                    dataGridSx={ecosystemGridSx}
                    tableMinWidth={1320}
                    rowHeight={60}
                    onPaginationChange={(page, pageSize) => {
                        setCurrentPagemain(page);
                        setrowsperpagemain(pageSize);
                    }}
                />
            </Box>
            {loading && <Loader />}

            <Box textAlign={'end'}>
                <AlertToast open={openAlert && alert === 'true'} onClose={alertClose} message={responseMsg} variant="success" />
            </Box>
        </>
    );
}
